'use client';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSearchParams } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import Link from 'next/link';
import React from 'react';
import { z } from 'zod';
import { resLoginSchema } from '@/lib/validations/customer/authSchemas';
import { login } from '@/clients/restaurant/auth';
import { useUserStore } from '@/stores/restuarant/user';
import InputField from '@/components/shared/InputField';
import { getDeviceToken, handleErrors } from '@/lib/helpers/functions';
import { LoadingSpinner } from '@ui/LoadingSpinner';
import { Arrow2 } from '@ui/Icons';
import { storeFront } from '@/lib/utils/routeLinks';
import useRestaurantAuth from '@/hooks/useRestaurantAuth';

export const RestaurantLoginForm = () => {
  const setUser = useUserStore((state) => state.setFetchedUser);
  const queryParams = useSearchParams().get('redirect');
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const { handleRestaurantLogin } = useRestaurantAuth();

  const t = useTranslations();
  const lang = useLocale();
  const LOGIN = `res_panel.login`;
  const schema = resLoginSchema(lang as 'de' | 'en');
  type FormData = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      username: '',
      password: '',
    },
  });

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    setIsLoading(true);
    const deviceToken = getDeviceToken();
    if (deviceToken) {
      data['device_token'] = deviceToken;
    }

    try {
      const res = await login(data);
      await handleRestaurantLogin(res, setUser);
      setIsLoading(false);
      // Navigation happens inside handleRestaurantLogin after user data is set
    } catch (error: any) {
      setIsLoading(false);
      handleErrors({ type: 'form', setError, errors: error.response });
    }
  };

  let signupLink = storeFront.registerBusiness.de();
  if (queryParams) {
    signupLink = storeFront.registerBusiness.de({ redirect: queryParams });
  }

  return (
    <form
      className="mb-6 w-full 2xl:mb-[30px]"
      onSubmit={handleSubmit(onSubmit)}
    >
      <InputField<FormData>
        name="username"
        type="email"
        label={t('email')}
        placeholder="sample@name.com"
        register={register}
        errors={errors}
      />
      <InputField<FormData>
        name="password"
        type="password"
        label={t('password')}
        placeholder="***************"
        register={register}
        errors={errors}
      />

      <div className="mb-[28px] flex w-full items-center justify-between 2xl:mb-[40px]">
        <Link
          href={storeFront.restaurantForgotPassword.de()}
          className="p-md hover:text-primary-700"
        >
          {t('forgot_password')}
        </Link>
      </div>
      <button
        type="submit"
        className="flex h-[58px] w-full items-center justify-center rounded-[40px] bg-gradient-to-r from-[#F5375C] to-[#C33076] align-baseline text-h5 font-semibold text-white 2xl:h-[68px]"
      >
        <span className="flex items-center gap-x-[5px]">
          {isLoading && <LoadingSpinner />}
          {t('login')} <Arrow2 className="ml-2" />
        </span>
      </button>
      <div className="mt-10 w-full whitespace-nowrap text-center">
        <Link href={signupLink} className="underline">
          {t(`${LOGIN}.register_btn`)}
        </Link>
        {/* <a href="mailto:support@lentho.com" className="underline">
          {t(`${LOGIN}.register_btn`)}
        </a> */}
      </div>
    </form>
  );
};
